import {useCallback, useEffect, useRef, useState} from 'react';
import {flushSync} from 'react-dom';
import {AUTO_SAVE_ENABLED, AUTO_SAVE_IDLE_MS} from '@/config/autoSaveConfig';

export type IdleAutoSaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

export interface UseIdleAutoSaveOptions {
  /** Persist callback. Throw or return false to mark error / keep dirty. */
  onSave: () => Promise<boolean | void>;
  /** When false, idle timer never fires save. */
  enabled?: boolean;
  /** Milliseconds between dirty and auto-save. Defaults to VITE_AUTO_SAVE_IDLE_MS (60s). */
  idleMs?: number;
  /** Unused (kept for callers). Auto-save is interval-based, not activity-idle. */
  target?: HTMLElement | Document | null;
}

export interface UseIdleAutoSaveResult {
  status: IdleAutoSaveStatus;
  isDirty: boolean;
  markDirty: () => void;
  markClean: () => void;
  /** Show pending UI without marking dirty (e.g. WP dialog open). */
  armPendingDisplay: () => void;
  lastSavedAt: Date | null;
}

/**
 * MoneyField / InputField keep a local draft until blur.
 * Auto-save must commit that draft first, otherwise persist writes stale store data
 * and still reports success.
 */
async function commitActiveInputForAutoSave(): Promise<void> {
  const el = document.activeElement;
  if (!(el instanceof HTMLElement)) return;
  if (el === document.body || el === document.documentElement) return;
  const isEditable =
    el instanceof HTMLInputElement
    || el instanceof HTMLTextAreaElement
    || el.isContentEditable
    || el.getAttribute('contenteditable') === 'true';
  if (!isEditable) return;
  try {
    flushSync(() => {
      el.blur();
    });
  } catch {
    el.blur();
  }
  await new Promise<void>((resolve) => {
    setTimeout(resolve, 0);
  });
}

/**
 * While dirty, persist once per `idleMs` (default 60s).
 *
 * Timer starts on the first `markDirty` and is NOT reset by mouse/scroll/click
 * or later keystrokes — those used to postpone save forever while the user
 * was still looking at the page.
 *
 * `markDirty` keeps ref + timer updates synchronous, but defers React setState
 * so native `input` listeners can call it without swallowing keystrokes
 * (React #13424).
 */
export function useIdleAutoSave({
  onSave,
  enabled = true,
  idleMs = AUTO_SAVE_IDLE_MS,
}: UseIdleAutoSaveOptions): UseIdleAutoSaveResult {
  const [status, setStatus] = useState<IdleAutoSaveStatus>('idle');
  const [isDirty, setIsDirty] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<Date | null>(null);

  const isDirtyRef = useRef(false);
  const isSavingRef = useRef(false);
  const onSaveRef = useRef(onSave);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const dirtyUiTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const scheduleIdleSaveRef = useRef<() => void>(() => {});

  onSaveRef.current = onSave;

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
    }
    idleTimerRef.current = null;
  }, []);

  const clearDirtyUiTimer = useCallback(() => {
    if (dirtyUiTimerRef.current) {
      clearTimeout(dirtyUiTimerRef.current);
    }
    dirtyUiTimerRef.current = null;
  }, []);

  const runSave = useCallback(async () => {
    if (isSavingRef.current || !isDirtyRef.current) return;
    await commitActiveInputForAutoSave();
    if (isSavingRef.current || !isDirtyRef.current) return;
    isSavingRef.current = true;
    // Consume dirty now so markDirty during the request can re-arm.
    isDirtyRef.current = false;
    clearDirtyUiTimer();
    setStatus('saving');
    try {
      const result = await onSaveRef.current();
      if (result === false) {
        isDirtyRef.current = true;
        setIsDirty(true);
        setStatus('error');
      } else {
        setLastSavedAt(new Date());
        if (isDirtyRef.current) {
          setIsDirty(true);
          setStatus('pending');
        } else {
          setIsDirty(false);
          setStatus('saved');
        }
      }
    } catch {
      isDirtyRef.current = true;
      setIsDirty(true);
      setStatus('error');
    } finally {
      isSavingRef.current = false;
      if (isDirtyRef.current) {
        scheduleIdleSaveRef.current();
      }
    }
  }, [clearDirtyUiTimer]);

  /** Arm the once-per-idleMs timer if none is running. Does not reset an existing countdown. */
  const scheduleIdleSave = useCallback(() => {
    if (!AUTO_SAVE_ENABLED || !enabled || !isDirtyRef.current || isSavingRef.current) {
      return;
    }
    if (idleTimerRef.current) return;
    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null;
      void runSave();
    }, idleMs);
  }, [enabled, idleMs, runSave]);

  scheduleIdleSaveRef.current = scheduleIdleSave;

  /**
   * Sync: dirty ref + start timer if needed.
   * Async (setTimeout 0): isDirty / status UI — avoids controlled-input first-key loss.
   */
  const markDirty = useCallback(() => {
    isDirtyRef.current = true;
    if (AUTO_SAVE_ENABLED && enabled) {
      scheduleIdleSave();
    }
    clearDirtyUiTimer();
    dirtyUiTimerRef.current = setTimeout(() => {
      dirtyUiTimerRef.current = null;
      setIsDirty(true);
      setStatus((prev) => (prev === 'saving' ? prev : 'pending'));
    }, 0);
  }, [clearDirtyUiTimer, enabled, scheduleIdleSave]);

  const markClean = useCallback(() => {
    isDirtyRef.current = false;
    clearDirtyUiTimer();
    setIsDirty(false);
    clearIdleTimer();
    setStatus('idle');
  }, [clearDirtyUiTimer, clearIdleTimer]);

  /** Show "Unsaved changes…" on open without starting idle save until real edits. */
  const armPendingDisplay = useCallback(() => {
    setStatus((prev) => (prev === 'saving' ? prev : 'pending'));
  }, []);

  useEffect(() => {
    if (!AUTO_SAVE_ENABLED || !enabled) {
      clearIdleTimer();
      return;
    }
    if (isDirtyRef.current) {
      scheduleIdleSave();
    }
  }, [clearIdleTimer, enabled, scheduleIdleSave]);

  return {
    status,
    isDirty,
    markDirty,
    markClean,
    armPendingDisplay,
    lastSavedAt,
  };
}
